import { Modal } from "@/frontend/shared/ui/Modal";
import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./AddUserModal.module.scss";
import { useState } from "react";
import { Input } from "@/frontend/shared/ui/Input";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { postInviteUser } from "../../model/services/postInviteUser";
import { EPostHogEventName } from "@/frontend/features/analytics/const/const";
import { fireAnalyticsEvent } from "@/frontend/features/analytics";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useQueryClient } from "@tanstack/react-query";

export function AddUserModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [inputError, setInputError] = useState("");

  const queryClient = useQueryClient();

  async function addUser() {
    if (email?.length === 0) return setInputError("Email is too small");
    if (!email?.includes("@")) return setInputError("Email is not valid");

    try {
      await postInviteUser({ email });
      snackbarConstructor("User invited successfully", "normal");
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>;
      reportErrorToSentry(axiosError, "INVITE USER: /organizations/invite");
      snackbarConstructor(axiosError?.response?.data.message || "Failed to send invitation", "error");
    } finally {
      setEmail("");
      setInputError("");
      fireAnalyticsEvent(EPostHogEventName.INVITE_INVESTOR_TO_ORGANIZATION);
      queryClient.invalidateQueries({ queryKey: ["usersData"] });
      onClose();
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setInputError("");
        setEmail("");
      }}
      title="Add User"
    >
      <Typography className={styles.descriptionText} variant="body-2" color="greyscale700">
        Users will receive an email with instructions on how to sign up and join your organization
      </Typography>
      <Input
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setInputError("");
        }}
        placeholder="Email"
        description="Add emails, separated by commas"
        className={styles.input}
        error={inputError}
      />
      <div className={styles.buttonsContainer}>
        <div className={styles.divider} />
        <NormalButton
          size="medium"
          onClick={() => {
            addUser();
          }}
        >
          Add User
        </NormalButton>
      </div>
    </Modal>
  );
}
